/**
 * The page's React tree (integration plan slice 6b): `App` shows whichever
 * mode the flow is in (`src/flow.ts`): a walkable scene's shell or a run's.
 * Both are drawn with the retro kit of `@ab/ui` (one panel, one cursor, one
 * menu; a word, not a sentence) and share the pause menu (`PauseMenu`:
 * graphics, the time of day in a scene (ADR 0032), language, stats, close),
 * opened by Escape, the menu button in the
 * top-right corner, or, in the dungeon, nothing else; the pad reaches an open
 * menu or dialogue through a `MenuNav`.
 *
 * The run's shell (`Shell`): the band (status, the collapsed log and the help
 * line in one corner panel, the minimap beside it), the labels, badges,
 * banners and route marker over the dungeon, the keyboard, the touch pad
 * (with `touch`: a coarse pointer, a touch screen or `?pad=1`; the help line
 * then describes touch) and the stats panel. Any key and any pad press (the
 * turn toggle's too) stop a running route after its step in progress; the
 * key's or button's own command is then sent (the controller's `input`), not
 * lost. While the pause menu is open, keys go to it and the pad's up and down
 * move its cursor, its centre (wait) confirms and attack closes it. When the
 * run ends (completed, failed, escaped) the pause menu closes before the end
 * banner shows and does not open again (the end banner is the one window
 * then; it sits above every other anyway).
 *
 * The scene's shell (`SceneShell`): no band. The menu button, the prompt (one
 * word when something is in reach; E or Space acts, so does the pad's centre
 * and a tap on the prompt), the location's name on each arrival, a dialogue
 * or a shop's menu when one is open (the shop's goods and prices are
 * read-only rows, dimmed and passed over by the cursor, with Leave the one
 * action: ADR 0032), the route marker, the portal's veil and, while boarding
 * the airship, the banner "Flying to {place}" over it.
 * One window at a time (`packages/ui/AGENTS.md`): while a dialogue, a shop or
 * a crossing's fade is up, Escape belongs to that window (a fade ignores it)
 * and the menu button waits; the pause menu opens only over the bare scene.
 *
 * The mailbox (slice 8c, ADR 0039): in a scene, logged in, the pause menu has
 * a Mail row with the unread count as its value; it lists the mails (the
 * template's title, newest first, `*` while unread, Claimed once claimed),
 * read again from the server as it opens (the list it had meanwhile; a failed
 * read is its first line with Try again); a mail opens (marked read) to who it
 * is from (the crew, the welcome gift, the ship), its text and what it
 * carries, with Claim while it has something unclaimed, and Back. A claim
 * closes the menus and shows "{what} received" over the scene; `storage_full`
 * says "No room aboard." as the mail's first line, any other refusal its
 * server line; a claim granted whose reads failed keeps its line and offers
 * Try again (which reads again, never claims again).
 * An incubating egg's menu shows the time left, the tickets held by tier
 * and Skip with the tickets the server picked (slice 8e, ADR 0040: "Skip
 * (2 hr + 30 min)", dimmed with "Not enough tickets." when the wallet
 * cannot cover it); since slice 8h (ADR 0042) Skip opens a confirmation:
 * "Use 2 hr + 30 min" (the game's choice), "Use 2 hr ×1" for each tier
 * alone that covers the wait, Cancel, with "40 min left" under the rows and
 * "Wastes 1 hr 20 min." for the row under the cursor; confirming one spends
 * exactly it. An unclaimed mail with an expiry says "Expires {date}";
 * a pinned one shows
 * "Kept" in the list's value column.
 *
 * The packing panel (slice 8f, ADR 0041; its fix round): a destination at the
 * wheel opens "Bag" (its tab), the lines "Slots {n}/{slots}" and "The bag is
 * full." or "Check the bag." above the rows (never scrolled away), the bag's
 * stacks (the cursor starts on the first; a count changes with left and
 * right, the pad's, or a tap on − or +, down to "×0", and a row confirmed at
 * "×0" goes back aboard), the heading "Aboard" over storage's items a bag
 * takes, then Auto, Empty, Go and Cancel.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { type Mail, type MailGrant, type ShownError, type TicketCounts, mailTemplateParams } from '@ab/api-contract';
import type { Command, Dir, Pos, RunState } from '@ab/game-core';
import { type QualityTier, qualityTiers } from '@ab/renderer';
import { type Locale, type Strings, type TextArgs, locales, partyFromRoster } from '@ab/session';
import {
  Banner,
  Dialogue,
  DirectionPad,
  EventLog,
  FloatingLabels,
  Hud,
  KeyHelp,
  LocationName,
  Login,
  type LoginAction,
  Menu,
  NewPassword,
  MenuButton,
  type MenuNav,
  type MenuLine,
  type MenuRow,
  type NavCommand,
  Minimap,
  Prompt,
  RouteMarker,
  RunStatus,
  StatsPanel,
  StatusBadges,
  createMenuNav,
  formatText,
  languageKey,
  text,
  uiTiming,
} from '@ab/ui';
import { pack } from 'virtual:ab-content';
import type { Controller } from './controller.ts';
import type { Flow } from './flow.ts';
import type { SceneController, SceneOverlay, SkipChoice } from './scene-controller.ts';
import { commandForKey } from './keys.ts';
import { statsReport } from './stats-report.ts';
import { timeZoneChoices } from './time-zones.ts';
import { rememberTier } from './tier.ts';
import type { AccountStore } from './account.ts';
import { secondsOf } from './clock.ts';
import { aboard, bagSlots, flown, stackLimit } from './pack.ts';
import { rememberTime } from './time.ts';

/** How often the stats panel refreshes, ms. */
const statsEveryMs = 500;

const copyText = (json: string): Promise<void> => navigator.clipboard.writeText(json);

/** What every shell shares: the flow (the page's locale, tier and stats panel), the input kind, the language switch. */
export type PageProps = {
  readonly flow: Flow;
  readonly touch: boolean;
  /** A language chosen in the pause menu (the boot code switches, remembers and retitles). */
  readonly onLanguage: (locale: Locale) => void;
  /** The account (ADR 0034): the login, the pause menu's party (the roster) and its log out. */
  readonly account: AccountStore;
};

export const App = (page: PageProps) => {
  const snap = useSyncExternalStore(page.flow.subscribe, page.flow.snapshot);
  const mode = snap.mode;
  return mode.kind === 'run' ? <Shell key={mode.id} controller={mode.run} page={page} stats={snap.stats} /> : <SceneShell key={mode.id} controller={mode.scene} page={page} stats={snap.stats} />;
};

/** The next of a list after `current`, round (the pause menu's rows cycle their values). */
const after = <T,>(list: readonly T[], current: T): T => list[(list.indexOf(current) + 1) % list.length] ?? current;

type PauseMenuProps = {
  readonly strings: Strings;
  readonly locale: Locale;
  readonly tier: QualityTier;
  /** The scene's time of day and its presets in order (ADR 0032, 0033); none in the dungeon, which has no row for it. */
  readonly time?: { readonly current: string; readonly presets: readonly string[] };
  readonly stats: boolean;
  readonly nav: MenuNav;
  readonly page: PageProps;
  readonly onClose: () => void;
  /** Offer the log out (a scene, logged in; never mid-run, ADR 0034). */
  readonly logOut?: boolean;
  /** Offer the mailbox (a scene, logged in; slice 8c), and where a claim's banner shows. */
  readonly mail?: { readonly banner: (key: string, values: Readonly<Record<string, string | number>>) => void };
};

/** Skip tickets of a tier, "2-hour ticket ×3" (slice 8e: the tier by its name, `ui.ticket.<tier>`). */
const ticketLine = (strings: Strings, tier: string, count: number): string => formatText(strings, 'ui.mail.tickets', { keys: { tier: `ui.ticket.${tier}` }, values: { count } });

/** What a mail carries (or a claim granted), one line each in the table's words. */
const attachmentLines = (strings: Strings, mail: Mail): string[] =>
  mail.attachments.map((a) => {
    switch (a.kind) {
      case 'item':
        return formatText(strings, 'ui.mail.item', { names: { item: a.item }, values: { count: a.count } });
      case 'egg':
        return formatText(strings, 'ui.mail.egg', { names: { monster: a.species } });
      case 'tickets':
        return ticketLine(strings, a.tier, a.count);
    }
  });
const grantedWords = (strings: Strings, granted: MailGrant): string[] => [
  ...granted.items.map((i) => formatText(strings, 'ui.mail.item', { names: { item: i.item }, values: { count: i.count } })),
  ...granted.eggs.map((e) => formatText(strings, 'ui.mail.egg', { names: { monster: e.species } })),
  ...Object.entries(granted.tickets).flatMap(([tier, n]) => (n > 0 ? [ticketLine(strings, tier, n)] : [])),
];
/**
 * A mail's parameters as its text's words: numbers as they are, content ids by their names, a ticket tier by its name
 * (`ui.ticket.<tier>`, the contract's `tier` kind); never free text.
 */
const mailArgs = (mail: Mail): TextArgs => {
  const kinds: Readonly<Record<string, string>> = mailTemplateParams[mail.template];
  const values: Record<string, number | string> = {};
  const names: Record<string, string> = {};
  const keys: Record<string, string> = {};
  for (const [k, v] of Object.entries(mail.params)) {
    if (typeof v === 'number') values[k] = v;
    else if (kinds[k] === 'tier') keys[k] = `ui.ticket.${v}`;
    // A time zone (slice 8h) is shown by its name as the server keeps it ("Europe/Paris").
    else if (kinds[k] === 'time_zone') values[k] = v;
    else names[k] = v;
  }
  return { values, names, keys };
};
/** A local date, no time (a mail's last day: `ui.mail.until`), in the page's language. */
const localDate = (iso: string, locale: Locale): string => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(secondsOf(iso) * 1000);
/**
 * Skip tickets by tier in their short names (slice 8e, ADR 0040 D1), the tiers in the wallet's order (the server's,
 * shortest first): a skip's plan largest first and joined by `ui.hatchery.plus` ("2 hr + 30 min", "30 min ×2"); what
 * the wallet holds shortest first, as a list ("30 min ×2, 2 hr ×1"; empty when it holds none).
 */
const ticketWords = (strings: Strings, order: readonly string[], tickets: TicketCounts, plan: boolean): string => {
  const tiers = (plan ? [...order].reverse() : order).filter((t) => (tickets[t] ?? 0) > 0);
  const one = (t: string): string => {
    const n = tickets[t] ?? 0;
    return plan && n === 1 ? text(strings, `ui.ticket_short.${t}`) : formatText(strings, 'ui.hatchery.ticket_count', { keys: { tier: `ui.ticket_short.${t}` }, values: { count: n } });
  };
  return tiers.map(one).join(text(strings, plan ? 'ui.hatchery.plus' : 'ui.label.separator'));
};

/**
 * One mail (slice 8c): "From the crew", its text and what it carries as read-only rows, then Claim (while unclaimed and
 * carrying something) and Back. Its title is the template's. A refusal is its first line.
 */
const senderKey: Readonly<Record<Mail['from'], string>> = { crew: 'ui.mail.from_crew', gift: 'ui.mail.from_gift', ship: 'ui.mail.from_ship' };

const MailView = ({
  strings,
  locale,
  page,
  nav,
  mail,
  onBack,
  onClaimed,
}: {
  readonly strings: Strings;
  readonly locale: Locale;
  readonly page: PageProps;
  readonly nav: MenuNav;
  readonly mail: Mail;
  readonly onBack: () => void;
  readonly onClaimed: (granted: MailGrant) => void;
}) => {
  const [line, setLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Granted, but the account's or the mail's read failed: Try again reads again (the claim stands).
  const pending = page.account.claimPending(mail.id);
  const claimable = (mail.claimedAt === null && mail.attachments.length > 0) || pending;
  const rows: readonly MenuRow[] = [
    ...(line === null ? [] : [{ key: 'line', label: text(strings, line), disabled: true }]),
    { key: 'from', label: text(strings, senderKey[mail.from]), disabled: true },
    // Slice 8e (Q86): an unclaimed mail with an expiry says its last day.
    ...(mail.expiresAt === null || mail.claimedAt !== null ? [] : [{ key: 'until', label: formatText(strings, 'ui.mail.until', { values: { date: localDate(mail.expiresAt, locale) } }), disabled: true }]),
    { key: 'body', label: formatText(strings, `${mail.template}_body`, mailArgs(mail)), disabled: true },
    ...attachmentLines(strings, mail).map((l, i) => ({ key: `a${i}`, label: l, disabled: true, ...(mail.claimedAt === null ? {} : { value: text(strings, 'ui.mail.claimed') }) })),
    ...(claimable ? [{ key: 'claim', label: text(strings, pending ? 'ui.server.retry' : 'ui.mail.claim'), disabled: busy }] : []),
    { key: 'back', label: text(strings, 'ui.mail.back') },
  ];
  return (
    <Menu
      key={`${mail.id}-${line ?? ''}-${String(busy)}`}
      strings={strings}
      title={formatText(strings, mail.template, mailArgs(mail))}
      rows={rows}
      onConfirm={(key) => {
        if (key !== 'claim') {
          onBack();
          return;
        }
        setBusy(true);
        setLine(null);
        void page.account.claimMail(mail.id).then((r) => {
          setBusy(false);
          if (r.ok) onClaimed(r.granted);
          else if (r.error !== 'unauthorized') setLine(r.error === 'storage_full' ? 'ui.mail.storage_full' : `ui.server.${r.error}`);
        });
      }}
      onClose={onBack}
      nav={nav}
      testId="mail-view"
    />
  );
};

/**
 * The mailbox (slice 8c): the mails newest first (`*` while unread, Claimed once claimed), then Back. The list is read
 * again as it opens (the one it had shows meanwhile); a failed read is the first line, with Try again.
 */
const MailMenu = ({
  strings,
  locale,
  page,
  nav,
  onClose,
  onClaimed,
}: {
  readonly strings: Strings;
  readonly locale: Locale;
  readonly page: PageProps;
  readonly nav: MenuNav;
  readonly onClose: () => void;
  readonly onClaimed: (granted: MailGrant) => void;
}) => {
  const box = useSyncExternalStore((l: () => void) => page.account.subscribe(l), () => page.account.mail());
  const [open, setOpen] = useState<string | null>(null);
  const [failed, setFailed] = useState<ShownError | null>(null);
  const [reads, setReads] = useState(0);
  useEffect(() => {
    let live = true;
    void page.account.loadMail().then((error) => {
      if (live) setFailed(error === 'unauthorized' ? null : error);
    });
    return () => {
      live = false;
    };
  }, [page.account, reads]);
  const opened = open === null ? undefined : box.mails.find((m) => m.id === open);
  if (opened !== undefined)
    return (
      <MailView
        strings={strings}
        locale={locale}
        page={page}
        nav={nav}
        mail={opened}
        onBack={() => {
          setOpen(null);
        }}
        onClaimed={onClaimed}
      />
    );
  const rows: readonly MenuRow[] = [
    ...(failed === null ? [] : [{ key: 'line', label: text(strings, `ui.server.${failed}`), disabled: true }]),
    ...(box.mails.length === 0 && failed === null ? [{ key: 'empty', label: text(strings, 'ui.mail.empty'), disabled: true }] : []),
    ...box.mails.map((m) => ({
      key: m.id,
      label: formatText(strings, m.template, mailArgs(m)),
      // The value column: unread first, then kept (pinned, slice 8e), then claimed.
      ...(m.readAt === null
        ? { value: text(strings, 'ui.mail.unread') }
        : m.pinned
          ? { value: text(strings, 'ui.mail.pinned') }
          : m.claimedAt !== null && m.attachments.length > 0
            ? { value: text(strings, 'ui.mail.claimed') }
            : {}),
    })),
    ...(failed === null ? [] : [{ key: 'retry', label: text(strings, 'ui.server.retry') }]),
    { key: 'back', label: text(strings, 'ui.mail.back') },
  ];
  return (
    <Menu
      key={failed ?? 'read'}
      strings={strings}
      title={text(strings, 'ui.menu.mail')}
      rows={rows}
      onConfirm={(key) => {
        if (key === 'back') {
          onClose();
          return;
        }
        if (key === 'retry') {
          setFailed(null);
          setReads((n) => n + 1);
          return;
        }
        setOpen(key);
        if (box.mails.find((m) => m.id === key)?.readAt === null) void page.account.openMail(key);
      }}
      onClose={onClose}
      nav={nav}
      testId="mail-menu"
    />
  );
};

/** Log out, asked first (ADR 0034): a two-row menu, the account's rows; no title (the pause menu's row said it). */
const LogOutMenu = ({ strings, page, nav, onClose }: { readonly strings: Strings; readonly page: PageProps; readonly nav: MenuNav; readonly onClose: () => void }) => (
  <Menu
    strings={strings}
    label={text(strings, 'ui.menu.log_out')}
    rows={[
      { key: 'log_out', label: text(strings, 'ui.account.log_out') },
      { key: 'cancel', label: text(strings, 'ui.account.cancel') },
    ]}
    onConfirm={(key) => {
      onClose();
      if (key === 'log_out') void page.account.logOut();
    }}
    onClose={onClose}
    nav={nav}
    testId="log-out"
  />
);

/**
 * The party (slice 7a, ADR 0033): Brad and the airship's monsters in the order they joined, each with its level; the
 * next run's party (`partyFromRoster`) as ordinary rows, the rest dimmed. Read-only: nothing to change yet.
 */
const PartyMenu = ({ strings, page, nav, onClose }: { readonly strings: Strings; readonly page: PageProps; readonly nav: MenuNav; readonly onClose: () => void }) => {
  const roster = useSyncExternalStore((l: () => void) => page.account.subscribe(l), () => page.account.state().roster);
  const leader = pack.rules.newPlayer.defaultParty[0];
  const party = leader !== undefined && 'equipment' in leader ? partyFromRoster(leader, roster, pack.monsters, pack.rules.combat.partySize) : [];
  const rows: readonly MenuRow[] = [
    ...(leader === undefined ? [] : [{ key: 'leader', label: text(strings, `${leader.monster}.name`), value: formatText(strings, 'ui.party.level', { values: { level: leader.level } }) }]),
    ...roster.map((m, i) => ({ key: m.id, label: text(strings, `${m.species}.name`), value: formatText(strings, 'ui.party.level', { values: { level: m.level } }), disabled: i + 1 >= party.length })),
    { key: 'close', label: text(strings, 'ui.menu.close') },
  ];
  return (
    <Menu
      strings={strings}
      title={text(strings, 'ui.party.title')}
      rows={rows}
      onConfirm={(key) => {
        if (key === 'close') onClose();
      }}
      onClose={onClose}
      nav={nav}
      testId="party-menu"
    />
  );
};

/**
 * The pause menu: the graphics tier, the time of day (a scene's only: its presets in order), the language and the
 * stats panel cycle in place (each choice remembered as before; the time per kind of scene, `ab.time.town` and
 * `ab.time.ship`); the party; close.
 */
const PauseMenu = ({ strings, locale, tier, time, stats, nav, page, onClose, logOut = false, mail }: PauseMenuProps) => {
  const [party, setParty] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [reading, setReading] = useState(false);
  const unread = useSyncExternalStore((l: () => void) => page.account.subscribe(l), () => page.account.mail().unread);
  if (leaving) return <LogOutMenu strings={strings} page={page} nav={nav} onClose={onClose} />;
  if (reading && mail !== undefined)
    return (
      <MailMenu
        strings={strings}
        locale={locale}
        page={page}
        nav={nav}
        onClose={() => {
          setReading(false);
        }}
        onClaimed={(granted) => {
          // The menus close; the banner says what came.
          onClose();
          mail.banner('ui.banner.claimed', { what: grantedWords(strings, granted).join(text(strings, 'ui.label.separator')) });
        }}
      />
    );
  if (party)
    return (
      <PartyMenu
        strings={strings}
        page={page}
        nav={nav}
        onClose={() => {
          setParty(false);
        }}
      />
    );
  const rows: readonly MenuRow[] = [
    { key: 'graphics', label: text(strings, 'ui.menu.graphics'), value: text(strings, `ui.status.tier_${tier}`) },
    ...(time === undefined || time.presets.length < 2 ? [] : [{ key: 'time', label: text(strings, 'ui.menu.time'), value: text(strings, `ui.menu.${time.current}`) }]),
    { key: 'language', label: text(strings, 'ui.menu.language'), value: text(strings, languageKey(locale)) },
    { key: 'stats', label: text(strings, 'ui.menu.stats'), value: text(strings, stats ? 'ui.menu.on' : 'ui.menu.off') },
    { key: 'party', label: text(strings, 'ui.menu.party') },
    ...(mail === undefined ? [] : [{ key: 'mail', label: text(strings, 'ui.menu.mail'), ...(unread > 0 ? { value: String(unread) } : {}) }]),
    ...(logOut ? [{ key: 'log_out', label: text(strings, 'ui.menu.log_out') }] : []),
    { key: 'close', label: text(strings, 'ui.menu.close') },
  ];
  const confirm = (key: string): void => {
    switch (key) {
      case 'graphics': {
        const next = after(qualityTiers, tier);
        page.flow.setTier(next);
        rememberTier(next);
        return;
      }
      case 'time': {
        if (time === undefined) return;
        const next = after(time.presets, time.current);
        const kind = page.flow.setTime(next);
        if (kind !== null) rememberTime(kind, next);
        return;
      }
      case 'language':
        page.onLanguage(after(locales, locale));
        return;
      case 'stats':
        page.flow.setStats(!stats);
        return;
      case 'party':
        setParty(true);
        return;
      case 'mail':
        setReading(true);
        return;
      case 'log_out':
        setLeaving(true);
        return;
      default:
        onClose();
    }
  };
  return <Menu strings={strings} label={text(strings, 'ui.menu.title')} rows={rows} onConfirm={confirm} onClose={onClose} nav={nav} testId="pause-menu" />;
};

export type ShellProps = { readonly controller: Controller; readonly page: PageProps; readonly stats: boolean };

const ended = (state: RunState): boolean => state.phase === 'completed' || state.phase === 'failed' || state.phase === 'escaped';

/** The pad's dungeon buttons while a menu is open: up and down move the cursor, the centre confirms, attack closes. */
const navOfCommand = (command: Command): NavCommand | null => {
  switch (command.type) {
    case 'move':
      return command.dir === 'n' ? 'up' : command.dir === 's' ? 'down' : null;
    case 'wait':
      return 'confirm';
    case 'attack':
      return 'close';
    case 'enter_floor':
    case 'turn':
    case 'descend':
    case 'use_skill':
    case 'use_item':
      return null;
  }
};

export const Shell = ({ controller, page, stats }: ShellProps) => {
  const snap = useSyncExternalStore(controller.subscribe, controller.snapshot);
  const strings = snap.strings;
  const [paused, setPaused] = useState(false);
  const pausedNow = useRef(paused);
  pausedNow.current = paused;
  const nav = useMemo(createMenuNav, []);
  /**
   * Signed out mid-run (a floor's submission answered `unauthorized`: the session was revoked or expired, ADR 0034):
   * the login shows over the run, which waits; once logged in, the floor is sent again (the login was the player's
   * tap). The run stays on the server meanwhile.
   */
  const status = useSyncExternalStore(
    (l: () => void) => page.account.subscribe(l),
    () => page.account.status(),
  );
  const mustChange = useSyncExternalStore(
    (l: () => void) => page.account.subscribe(l),
    () => page.account.mustChangePassword(),
  );
  const blocked = status === 'signed_out' || mustChange;
  const blockedNow = useRef(blocked);
  blockedNow.current = blocked;
  useEffect(() => {
    if (blocked) setPaused(false);
    else if (status === 'signed_in' && controller.snapshot().serverError === 'unauthorized') controller.retry();
  }, [blocked, status, controller]);
  // The end of the run closes the pause menu: the end banner is the one thing to read then.
  const over = ended(snap.state);
  useEffect(() => {
    if (over) setPaused(false);
  }, [over]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // An open menu took the key (it listens first); while it (or the login) is open nothing else does.
      if (e.defaultPrevented || pausedNow.current || blockedNow.current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        controller.cancelRoute();
        if (!ended(controller.snapshot().state)) setPaused(true);
        return;
      }
      const command = commandForKey(e.key, e.shiftKey);
      if (command === null || e.repeat) {
        controller.cancelRoute();
        return;
      }
      e.preventDefault();
      // Refused while an animation plays or after the run ended (the controller says so; nothing to show).
      controller.input(command);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [controller]);
  const read = useCallback(() => statsReport(controller.view, { locale: controller.snapshot().locale, pad: page.touch }), [controller, page.touch]);
  const locate = useCallback((unit: string) => controller.view.project(unit), [controller]);
  const locateTile = useCallback((pos: Pos) => controller.view.projectTile(pos), [controller]);
  const padCommand = useCallback(
    (command: Command) => {
      if (pausedNow.current || blockedNow.current || controller.snapshot().serverError !== null) {
        const n = navOfCommand(command);
        if (n !== null) nav.send(n);
        return;
      }
      controller.input(command);
    },
    [controller, nav],
  );
  const padInteract = useCallback(() => {
    controller.cancelRoute();
  }, [controller]);
  const ready = useCallback(() => !pausedNow.current && !blockedNow.current && !controller.busy(), [controller]);
  const togglePause = useCallback(() => {
    controller.cancelRoute();
    if (pausedNow.current) setPaused(false);
    else if (!ended(controller.snapshot().state)) setPaused(true);
  }, [controller]);
  const close = useCallback(() => {
    setPaused(false);
  }, []);
  return (
    <>
      <StatusBadges rows={snap.overlays.badges} strings={strings} locate={locate} />
      <FloatingLabels labels={snap.overlays.labels} strings={strings} />
      <Banner banner={snap.overlays.banner} strings={strings} />
      <RouteMarker target={snap.route} strings={strings} locate={locateTile} />
      {stats && <StatsPanel strings={strings} read={read} everyMs={statsEveryMs} copy={copyText} />}
      <Hud aside={<Minimap map={snap.map} strings={strings} />}>
        <RunStatus state={snap.state} content={pack} strings={strings} />
        <EventLog lines={snap.lines} strings={strings} />
        <KeyHelp strings={strings} touch={page.touch} />
      </Hud>
      <MenuButton strings={strings} open={paused} waiting={over && !paused} onPress={togglePause} />
      {page.touch && <DirectionPad strings={strings} onCommand={padCommand} onInteract={padInteract} ready={ready} />}
      {paused && !blocked && <PauseMenu strings={strings} locale={snap.locale} tier={snap.tier} stats={stats} nav={nav} page={page} onClose={close} />}
      {status === 'signed_out' ? <LoginScreen strings={strings} account={page.account} nav={nav} /> : mustChange ? <NewPasswordScreen strings={strings} account={page.account} nav={nav} /> : null}
      {snap.serverError !== null && !blocked && (
        // A floor the server refused or did not answer (ADR 0034): its line; closing it tries again (or gives the run up).
        <Dialogue
          strings={strings}
          speaker={null}
          text={text(strings, `ui.server.${snap.serverError}`)}
          more={false}
          onAdvance={() => {
            controller.retry();
          }}
          onClose={() => {
            controller.retry();
          }}
          nav={nav}
        />
      )}
    </>
  );
};

/** Keys that walk a scene: screen directions (x right, y up), WASD and the arrows. */
const walkKeys: Readonly<Record<string, readonly [number, number]>> = {
  w: [0, 1],
  arrowup: [0, 1],
  s: [0, -1],
  arrowdown: [0, -1],
  a: [-1, 0],
  arrowleft: [-1, 0],
  d: [1, 0],
  arrowright: [1, 0],
};

export type SceneShellProps = { readonly controller: SceneController; readonly page: PageProps; readonly stats: boolean };

/** What is open over the scene, drawn: a dialogue's page, a shop's menu, the airship's course or the eggs to place. */
const Overlay = ({ overlay, strings, controller, nav }: { readonly overlay: SceneOverlay; readonly strings: Strings; readonly controller: SceneController; readonly nav: MenuNav }) => {
  switch (overlay.kind) {
    case 'course': {
      // The airship's destinations (ADR 0033): no cost shown until the fuel and the flight timer of roadmap step 6.
      const rows: readonly MenuRow[] = [...overlay.destinations.map((d) => ({ key: d, label: text(strings, `${d}.name`) })), { key: 'cancel', label: text(strings, 'ui.flight.cancel') }];
      return (
        <Menu
          strings={strings}
          label={text(strings, 'ui.flight.title')}
          rows={rows}
          onConfirm={(row) => {
            controller.course(row);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="course"
        />
      );
    }
    case 'pack': {
      // The packing panel (slice 8f, ADR 0041; Q85; its fix round): its lines above the rows, always in view (the
      // slots in use, a refusal, no answer); the bag's stacks (left, right, − and + change a count, down to "×0"), the
      // heading "Aboard" and storage's items a bag takes with what is left of each, set in under it; then Auto, Empty,
      // Go (dimmed while storage is read, or after a third refusal) and Cancel. The cursor starts on the bag.
      const left = aboard(overlay.storage, overlay.bag);
      const count = (n: number): string => formatText(strings, 'ui.pack.count', { values: { count: n } });
      const lines: readonly MenuLine[] = [
        { key: 'header', text: formatText(strings, 'ui.pack.header', { values: { n: flown(overlay.bag).length, slots: bagSlots() } }) },
        ...(overlay.failed === null ? [] : [{ key: 'failed', text: text(strings, `ui.server.${overlay.failed}`) }]),
        ...(overlay.line === null ? [] : [{ key: 'line', text: text(strings, overlay.line) }]),
      ];
      const rows: readonly MenuRow[] = [
        ...overlay.bag.map((b) => ({
          key: `bag:${b.item}`,
          label: text(strings, `${b.item}.name`),
          value: b.count < 1 ? text(strings, 'ui.pack.out') : count(b.count),
          adjustable: true,
          range: { now: b.count, min: 0, max: stackLimit(overlay.storage, b.item) },
        })),
        { key: 'aboard', label: text(strings, 'ui.pack.storage'), heading: true },
        ...(left.length === 0 ? [{ key: 'none', label: text(strings, 'ui.pack.none'), disabled: true, under: 'aboard' }] : left.map((s) => ({ key: `store:${s.item}`, label: text(strings, `${s.item}.name`), value: count(s.count), under: 'aboard' }))),
        { key: 'auto', label: text(strings, 'ui.pack.auto') },
        { key: 'empty', label: text(strings, 'ui.pack.empty') },
        ...(overlay.failed === null ? [] : [{ key: 'retry', label: text(strings, 'ui.server.retry') }]),
        { key: 'go', label: text(strings, 'ui.pack.go'), disabled: overlay.stuck, waiting: overlay.reading },
        { key: 'cancel', label: text(strings, 'ui.flight.cancel') },
      ];
      const first = overlay.bag[0]?.item ?? left[0]?.item;
      return (
        <Menu
          // Its own instance: the course's menu before it at this place must not hand over its cursor.
          key="pack"
          strings={strings}
          title={text(strings, 'ui.pack.title')}
          rows={rows}
          lines={lines}
          {...(first === undefined ? {} : { initial: overlay.bag.length > 0 ? `bag:${first}` : `store:${first}` })}
          onConfirm={(row) => {
            controller.pack(row);
          }}
          onAdjust={(row, step) => {
            controller.packAdjust(row, step);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="pack"
        />
      );
    }
    case 'eggs': {
      const rows: readonly MenuRow[] = [
        ...overlay.eggs.map((e) => ({ key: e.species, label: formatText(strings, 'ui.hatch.egg', { names: { monster: e.species } }), value: formatText(strings, 'ui.hatch.count', { values: { count: e.count } }) })),
        { key: 'cancel', label: text(strings, 'ui.hatch.cancel') },
      ];
      return (
        <Menu
          strings={strings}
          label={text(strings, 'ui.hatch.eggs')}
          rows={rows}
          onConfirm={(row) => {
            controller.egg(row);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="eggs"
        />
      );
    }
    case 'incubating': {
      // An incubating egg (slice 8c; tiers since slice 8e, ADR 0040): its time left and the tickets held as read-only
      // rows, then Skip with the tickets the server picked ("Skip (2 hr + 30 min)"), dimmed with "Not enough tickets."
      // when the wallet cannot cover the time left.
      const order = Object.keys(overlay.tickets);
      const held = ticketWords(strings, order, overlay.tickets, false);
      const wallet = held === '' ? text(strings, 'ui.hatchery.tickets_none') : formatText(strings, 'ui.hatchery.tickets', { values: { tickets: held } });
      const rows: readonly MenuRow[] = [
        { key: 'time', label: formatText(strings, 'ui.hatch.remaining', { keys: { time: overlay.time } }), disabled: true },
        { key: 'tickets', label: wallet, disabled: true },
        { key: 'skip', label: formatText(strings, 'ui.hatchery.skip', { values: { tickets: ticketWords(strings, order, overlay.price.tickets, true) } }), disabled: !overlay.price.affordable },
        ...(overlay.price.affordable ? [] : [{ key: 'no_tickets', label: text(strings, 'ui.hatchery.no_tickets'), disabled: true }]),
        { key: 'cancel', label: text(strings, 'ui.hatch.cancel') },
      ];
      return (
        <Menu
          strings={strings}
          label={wallet}
          rows={rows}
          onConfirm={(row) => {
            controller.incubating(row);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="incubating"
        />
      );
    }
    case 'skip': {
      // The skip's confirmation (slice 8h, ADR 0042; Q88): the game's choice first ("Use 2 hr + 30 min"), each tier
      // alone that covers the wait ("Use 2 hr ×1"), Cancel; titled "Skip" in its tab. Above the rows, as a status line,
      // the time left ("40 min left"), after the refusal that brought it back if any; below them, for the row under
      // the cursor, what it wastes ("Wastes 1 hr 20 min.") in the warning colour, none when it wastes nothing. While the
      // account is read the rows wait (dimmed); a failed read shows its line and Try again instead of any row (the
      // fix round: never a stale choice).
      const order = Object.keys(overlay.tickets);
      const use = (c: SkipChoice): string => {
        const [tier, count] = Object.entries(c.plan.tickets)[0] ?? ['', 0];
        const words = c.auto ? ticketWords(strings, order, c.plan.tickets, true) : formatText(strings, 'ui.hatchery.ticket_count', { keys: { tier: `ui.ticket_short.${tier}` }, values: { count } });
        return formatText(strings, 'ui.hatchery.use', { values: { tickets: words } });
      };
      const rows: readonly MenuRow[] =
        overlay.failed === null
          ? [...overlay.choices.map((c) => ({ key: c.key, label: use(c), disabled: !c.plan.affordable, waiting: overlay.reading })), { key: 'cancel', label: text(strings, 'ui.hatch.cancel') }]
          : [
              { key: 'retry', label: text(strings, 'ui.server.retry'), waiting: overlay.reading },
              { key: 'cancel', label: text(strings, 'ui.hatch.cancel') },
            ];
      const status: readonly MenuLine[] = [
        ...(overlay.line === null ? [] : [{ key: 'line', text: text(strings, overlay.line) }]),
        overlay.failed === null ? { key: 'left', text: formatText(strings, 'ui.hatchery.left', { keys: { time: overlay.left } }) } : { key: 'failed', text: text(strings, `ui.server.${overlay.failed}`) },
      ];
      const below = (cursor: string | null): readonly MenuLine[] => {
        const waste = overlay.failed === null ? (overlay.choices.find((c) => c.key === cursor)?.waste ?? null) : null;
        return waste === null ? [] : [{ key: 'waste', text: formatText(strings, 'ui.hatchery.waste', { keys: { time: waste } }), tone: 'warning' as const }];
      };
      return (
        <Menu
          // Its own instance: the incubator's menu before it at this place must not hand over its cursor.
          key="skip"
          strings={strings}
          title={text(strings, 'ui.hatchery.confirm_title')}
          rows={rows}
          lines={status}
          below={below}
          onConfirm={(row) => {
            controller.skipChoice(row);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="skip"
        />
      );
    }
    case 'dialogue':
      return (
        <Dialogue
          strings={strings}
          speaker={overlay.speaker === null ? null : text(strings, overlay.speaker)}
          text={formatText(strings, overlay.pages[overlay.page] ?? '', overlay.args ?? {})}
          more={overlay.page + 1 < overlay.pages.length}
          onAdvance={() => {
            controller.advance();
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
        />
      );
    case 'shop': {
      // The goods and prices are read-only rows until the wallet (roadmap step 6, ADR 0032): dimmed, passed over by the cursor.
      const rows: readonly MenuRow[] = [
        ...overlay.offers.map((o) => ({ key: o.item, label: text(strings, `${o.item}.name`), value: formatText(strings, 'ui.shop.price', { values: { price: o.price } }), disabled: true })),
        { key: 'leave', label: text(strings, 'ui.shop.leave') },
      ];
      return (
        <Menu
          strings={strings}
          title={text(strings, overlay.keeper)}
          rows={rows}
          onConfirm={(row) => {
            controller.shop(row);
          }}
          onClose={() => {
            controller.close();
          }}
          nav={nav}
          testId="shop"
        />
      );
    }
  }
};

export const SceneShell = ({ controller, page, stats }: SceneShellProps) => {
  const snap = useSyncExternalStore(controller.subscribe, controller.snapshot);
  const strings = snap.strings;
  const [paused, setPausedState] = useState(false);
  const pausedNow = useRef(false);
  pausedNow.current = paused;
  /**
   * No account yet (the login shows), one being checked, or the run the server keeps not known yet (ADR 0034: the
   * town waits for `GET /api/runs/current`; no answer shows a retry row) or stuck (another build's run: its question
   * shows): the scene is held under it, as for a window.
   */
  const status = useSyncExternalStore(
    (l: () => void) => page.account.subscribe(l),
    () => page.account.status(),
  );
  const runCheck = useSyncExternalStore(
    (l: () => void) => page.account.subscribe(l),
    () => page.account.run(),
  );
  const mustChange = useSyncExternalStore(
    (l: () => void) => page.account.subscribe(l),
    () => page.account.mustChangePassword(),
  );
  const blocked =
    status === 'signed_out' || status === 'checking' || mustChange || runCheck.kind === 'checking' || runCheck.kind === 'failed' || (runCheck.kind === 'stuck' && runCheck.asking);
  const blockedNow = useRef(blocked);
  blockedNow.current = blocked;
  const open = useRef(false);
  open.current = paused || blocked || snap.overlay !== null;
  /** One window at a time: the pause menu waits while a dialogue, a shop, a crossing's fade or the login is up. */
  const waiting = !paused && (blocked || snap.overlay !== null || snap.fading || snap.flying !== null);
  const nav = useMemo(createMenuNav, []);
  const setPaused = useCallback(
    (next: boolean) => {
      setPausedState(next);
      controller.setPaused(next || blockedNow.current);
    },
    [controller],
  );
  useEffect(() => {
    // Signed out from anywhere (the pause menu, or any request answered `unauthorized`): the login is the one window.
    if (blocked) {
      setPausedState(false);
      if (controller.snapshot().overlay !== null) controller.close();
    }
    controller.setPaused(blocked);
  }, [controller, blocked]);
  useEffect(() => {
    const held = new Set<string>();
    const apply = (): void => {
      let x = 0;
      let y = 0;
      for (const k of held) {
        const d = walkKeys[k];
        if (d === undefined) continue;
        x += d[0];
        y += d[1];
      }
      controller.hold('keys', x === 0 && y === 0 ? null : { x: Math.sign(x), y: Math.sign(y) });
    };
    const down = (e: KeyboardEvent): void => {
      const key = e.key.toLowerCase();
      controller.setRun(e.shiftKey);
      // An open menu or dialogue took the key (it listens first); while one is open nothing else does.
      if (e.defaultPrevented || open.current) return;
      if (key === 'escape') {
        e.preventDefault();
        // A crossing's fade: no window opens until the new scene shows.
        if (controller.snapshot().fading || controller.snapshot().flying !== null) return;
        controller.cancelRoute();
        setPaused(true);
        return;
      }
      if (key === 'e' || key === ' ') {
        e.preventDefault();
        if (!e.repeat) controller.action();
        return;
      }
      if (walkKeys[key] === undefined) {
        if (!e.repeat && key !== 'shift') controller.cancelRoute();
        return;
      }
      e.preventDefault();
      held.add(key);
      apply();
    };
    const up = (e: KeyboardEvent): void => {
      controller.setRun(e.shiftKey);
      if (held.delete(e.key.toLowerCase())) apply();
    };
    const clear = (): void => {
      held.clear();
      controller.setRun(false);
      apply();
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, [controller, setPaused]);
  const read = useCallback(() => statsReport(controller.view, { locale: controller.snapshot().locale, pad: page.touch, time: controller.snapshot().lighting }), [controller, page.touch]);
  const routeHeight = snap.route?.h ?? 0;
  const locateGround = useCallback((pos: Pos) => controller.view.project(pos.x, pos.y, routeHeight), [controller, routeHeight]);
  const hold = useCallback(
    (dir: Dir | null) => {
      if (dir !== null && open.current) {
        // In a menu the pad's up and down move the cursor, left and right change an adjustable row (the packing
        // panel's counts, slice 8f); in a dialogue the directions do nothing.
        if (dir === 'n') nav.send('up');
        else if (dir === 's') nav.send('down');
        else if (dir === 'w') nav.send('left');
        else if (dir === 'e') nav.send('right');
        return;
      }
      controller.holdDir(dir);
    },
    [controller, nav],
  );
  const act = useCallback(() => {
    if (open.current) nav.send('confirm');
    else controller.action();
  }, [controller, nav]);
  const interact = useCallback(() => {
    controller.cancelRoute();
  }, [controller]);
  const nothing = useCallback(() => undefined, []);
  const always = useCallback(() => true, []);
  const togglePause = useCallback(() => {
    if (pausedNow.current) {
      setPaused(false);
      return;
    }
    const s = controller.snapshot();
    if (blockedNow.current || s.overlay !== null || s.fading || s.flying !== null) return;
    controller.cancelRoute();
    setPaused(true);
  }, [controller, setPaused]);
  const close = useCallback(() => {
    setPaused(false);
  }, [setPaused]);
  return (
    <>
      <RouteMarker target={snap.route} strings={strings} locate={locateGround} />
      {stats && <StatsPanel strings={strings} read={read} everyMs={statsEveryMs} copy={copyText} />}
      <div className="ab-fade" data-testid="scene-fade" data-on={snap.fading} style={{ transitionDuration: `${uiTiming.sceneFadeMs}ms` }} />
      <LocationName name={text(strings, snap.nameKey)} arrival={snap.arrival} />
      <Banner banner={snap.flying === null ? snap.banner : { id: -snap.arrival, key: 'ui.banner.flying_to', names: { place: snap.flying }, tone: 'plain', durationMs: uiTiming.flightMs }} strings={strings} />
      <MenuButton strings={strings} open={paused} waiting={waiting} onPress={togglePause} />
      <Prompt strings={strings} word={paused || blocked || snap.overlay !== null ? null : snap.prompt} keyHint={!page.touch} onAct={act} />
      {page.touch && <DirectionPad strings={strings} mode="continuous" onHold={hold} onAction={act} onCommand={nothing} onInteract={interact} ready={always} />}
      {snap.overlay !== null && <Overlay overlay={snap.overlay} strings={strings} controller={controller} nav={nav} />}
      {paused && <PauseMenu strings={strings} locale={snap.locale} tier={snap.tier} time={{ current: snap.lighting, presets: snap.lightings }} stats={stats} nav={nav} page={page} onClose={close} logOut={status === 'signed_in'} {...(status === 'signed_in' ? { mail: { banner: (key: string, values: Readonly<Record<string, string | number>>) => { controller.banner(key, values); } } } : {})} />}
      {status === 'signed_out' ? (
        <LoginScreen strings={strings} account={page.account} nav={nav} />
      ) : mustChange ? (
        <NewPasswordScreen strings={strings} account={page.account} nav={nav} />
      ) : runCheck.kind === 'failed' ? (
        <RunCheckFailed strings={strings} account={page.account} error={runCheck.error} nav={nav} />
      ) : runCheck.kind === 'stuck' && runCheck.asking ? (
        <RunStuck strings={strings} account={page.account} nav={nav} />
      ) : null}
    </>
  );
};

/**
 * The run the server keeps could not be asked for (ADR 0034): its line in the corner tab and one row, Try again; the
 * town waits under it (a new flight would abandon a run the page has not seen). No Cancel row: there is nothing to go
 * back to.
 */
const RunCheckFailed = ({ strings, account, error, nav }: { readonly strings: Strings; readonly account: AccountStore; readonly error: ShownError; readonly nav: MenuNav }) => (
  <Menu
    strings={strings}
    title={text(strings, `ui.server.${error}`)}
    rows={[{ key: 'retry', label: text(strings, 'ui.server.retry') }]}
    onConfirm={() => {
      void account.checkRun();
    }}
    onClose={() => undefined}
    nav={nav}
    testId="run-check"
  />
);

/**
 * A run the server keeps that this build cannot play (another `coreVersion` or content hash, ADR 0002): "The game needs
 * an update." with Give up the run and Cancel. Cancel keeps the run (flying stays refused and asks
 * again); Give up asks once more, then abandons it on the server (`POST /api/runs/:id/abandon`), the one explicit
 * give-up of the page.
 */
const RunStuck = ({ strings, account, nav }: { readonly strings: Strings; readonly account: AccountStore; readonly nav: MenuNav }) => {
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ShownError | null>(null);
  const dismiss = (): void => {
    account.dismissRunQuestion();
  };
  const rows: readonly MenuRow[] = [
    { key: 'give_up', label: text(strings, 'ui.server.give_up'), disabled: busy },
    { key: 'cancel', label: text(strings, 'ui.server.cancel') },
  ];
  if (!sure) {
    return (
      <Menu
        strings={strings}
        title={text(strings, 'ui.server.version_mismatch')}
        rows={rows}
        onConfirm={(key) => {
          if (key === 'give_up') setSure(true);
          else dismiss();
        }}
        onClose={dismiss}
        nav={nav}
        testId="run-stuck"
      />
    );
  }
  return (
    <Menu
      key="sure"
      strings={strings}
      title={text(strings, error === null ? 'ui.server.give_up_sure' : `ui.server.${error}`)}
      rows={rows}
      onConfirm={(key) => {
        if (key !== 'give_up') {
          dismiss();
          return;
        }
        setBusy(true);
        void account.giveUpRun().then((e) => {
          setBusy(false);
          setError(e);
        });
      }}
      onClose={dismiss}
      nav={nav}
      testId="run-give-up"
    />
  );
};

/**
 * The login over the scene (ADR 0034): log in or create the account (with its invite code, ADR 0038 D10); the
 * server's refusal (or no answer) as its `ui.server.<code>` line. Logged in, the account is read and the login goes.
 */
const LoginScreen = ({ strings, account, nav }: { readonly strings: Strings; readonly account: AccountStore; readonly nav: MenuNav }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ShownError | null>(() => account.lastError());
  const zones = useMemo(zoneChoices, []);
  const submit = useCallback(
    (action: LoginAction, name: string, password: string, invite: string, timeZone: string) => {
      setBusy(true);
      void (action === 'log_in' ? account.logIn(name, password) : account.create(name, password, invite, timeZone)).then((e) => {
        setBusy(false);
        if (e !== null) setError(e);
      });
    },
    [account],
  );
  return (
    <Login
      strings={strings}
      title={text(strings, 'ui.shell.title')}
      // A zone the server does not know (the fix round): the login asks for another.
      error={error === null ? null : text(strings, error === 'bad_time_zone' ? 'ui.login.bad_time_zone' : `ui.server.${error}`)}
      busy={busy}
      onSubmit={submit}
      onDismissError={() => {
        setError(null);
      }}
      timeZones={zones.zones}
      timeZone={zones.device}
      nav={nav}
    />
  );
};

/**
 * New account's time zones (slice 8h, ADR 0042; Q87; its fix round): `time-zones.ts` over this page's `Intl` (the
 * browser's zones without `Etc/*`, `UTC` once; the device's zone selected only when listed, else `UTC`; every `Intl`
 * call guarded). The server checks the choice against its own `Intl`.
 */
const zoneChoices = (): ReturnType<typeof timeZoneChoices> => timeZoneChoices(typeof Intl === 'undefined' ? undefined : Intl);

/**
 * A temporary password from the crew (ADR 0038 D8): before the town, the new password twice (`NewPassword`); the
 * temporary one again says `ui.login.same_password`, the server's refusal (a password too short, no answer) its
 * `ui.server.<code>` line. Saved, the panel goes at once (the account is read again behind it).
 */
const NewPasswordScreen = ({ strings, account, nav }: { readonly strings: Strings; readonly account: AccountStore; readonly nav: MenuNav }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ShownError | 'same_password' | null>(null);
  const submit = useCallback(
    (password: string) => {
      setBusy(true);
      void account.changePassword(password).then((e) => {
        setBusy(false);
        if (e !== null) setError(e);
      });
    },
    [account],
  );
  return (
    <NewPassword
      strings={strings}
      error={error === null ? null : text(strings, error === 'same_password' ? 'ui.login.same_password' : `ui.server.${error}`)}
      busy={busy}
      onSubmit={submit}
      onDismissError={() => {
        setError(null);
      }}
      nav={nav}
    />
  );
};
